import { Fragment, useEffect, useState } from "react";
import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import SidebarItem from "./SidebarItem";
import ThemeToggle from "../ui/ThemeToggle";
import { getSolicitacoesCounts } from "../../services/solicitacoesService";

const MENU = [
  { id: "dashboard", icon: "📊", label: "Início" },
  { id: "agenda", icon: "📅", label: "Agenda" },
  { id: "roteiros", icon: "🗺️", label: "Roteiros" },
  { id: "clientes", icon: "👤", label: "Clientes" },
  { id: "fornecedores", icon: "🏨", label: "Fornecedores" },
  { id: "financeiro", icon: "💰", label: "Financeiro" },
  { id: "contas-receber", icon: "📥", label: "Contas a Receber" },
  { id: "contas-pagar", icon: "📤", label: "Contas a Pagar" },
  { id: "extrato", icon: "📒", label: "Extrato" },
  { id: "relatorios", icon: "📄", label: "Relatórios" },
  { id: "perfil", icon: "⚙️", label: "Meu Perfil" },
  { id: "site-publico", icon: "🌐", label: "Site Público" },
  { id: "solicitacoes", icon: "📩", label: "Solicitações" },
  // Owner-only: cabine admin do SaaS. Fica no fim, separada por divider.
  { id: "admin-guias", icon: "🛠️", label: "Admin TecmovelTurismo", ownerOnly: true },
];

export default function AppLayout({ page, onNavigate, children }) {
  const { C } = useTheme();
  const { logout, user, profile, isOwner } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [novasSolicitacoes, setNovasSolicitacoes] = useState(0);
  // Mobile: sidebar vira drawer (fora da tela por default; abre via ☰ no header
  // do conteúdo). Breakpoint 767px — telefones em portrait/landscape típicos.
  const [isMobile, setIsMobile] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  // Ao sair do mobile (ex: rotacionou pra landscape amplo), garante drawer fechado.
  useEffect(() => {
    if (!isMobile) setMobileOpen(false);
  }, [isMobile]);

  // Em mobile, cada navegação fecha o drawer — comportamento típico de app.
  function handleNavigate(id) {
    onNavigate(id);
    if (isMobile) setMobileOpen(false);
  }

  // Esconde itens ownerOnly de quem não é Owner. Backend já gateia com 403,
  // mas filtrar o menu é a primeira camada (UX + defense-in-depth).
  const visibleMenu = MENU.filter((m) => !m.ownerOnly || isOwner);

  // Badge de solicitações novas — polling a cada 60s enquanto a tela
  // estiver aberta. Ao abrir a própria página "solicitacoes" zeramos
  // localmente pra dar feedback imediato; o polling repõe no próximo tick.
  useEffect(() => {
    let cancelled = false;
    async function tick() {
      try {
        const counts = await getSolicitacoesCounts();
        if (!cancelled) setNovasSolicitacoes(counts?.nova ?? 0);
      } catch {
        // silencioso — badge não é crítico
      }
    }
    tick();
    const id = setInterval(tick, 60_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  // Branding no topo da sidebar: se o guia tem logo cadastrada, mostra; senão
  // mantém o aviãozinho como fallback visual. Texto é sempre Empresa → Nome
  // (duas linhas) ou só Nome (uma linha) ou "Sistema" como último fallback.
  const empresaLabel = profile?.empresa || profile?.nome || "Sistema";
  const mostraNome = !!(profile?.empresa && profile?.nome);

  // Em mobile, a sidebar vira drawer fixed que sai da tela; no desktop, mantém
  // o comportamento antigo (largura fixa, colapsável via ◀ ▶).
  const sidebarStyle = isMobile
    ? {
        position: "fixed",
        top: 0,
        left: mobileOpen ? 0 : "-100%",
        bottom: 0,
        width: 260,
        zIndex: 200,
        transition: "left 0.25s ease",
        boxShadow: mobileOpen ? "0 0 30px rgba(0,0,0,0.4)" : "none",
        background: C.sidebar,
        borderRight: `1px solid ${C.border}`,
        display: "flex",
        flexDirection: "column",
        padding: "16px 14px",
      }
    : {
        width: collapsed ? 64 : 220,
        background: C.sidebar,
        borderRight: `1px solid ${C.border}`,
        display: "flex",
        flexDirection: "column",
        padding: collapsed ? "16px 8px" : "16px 14px",
        transition: "all 0.3s ease",
        flexShrink: 0,
      };

  // No desktop mantém "collapsed"; em mobile ignoramos (drawer sempre expandido).
  const sidebarCollapsed = !isMobile && collapsed;

  return (
    <div
      style={{
        display: "flex",
        height: "100vh",
        background: C.bg,
        fontFamily: "'DM Sans', sans-serif",
        color: C.text,
        overflow: "hidden",
        transition: "background 0.3s ease, color 0.3s ease",
      }}
    >
      {/* Overlay do drawer mobile — clique fora fecha. */}
      {isMobile && mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            zIndex: 150,
          }}
        />
      )}

      {/* Sidebar */}
      <div style={sidebarStyle}>
        <div
          style={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: sidebarCollapsed ? "center" : "space-between",
            marginBottom: 28,
            padding: sidebarCollapsed ? 0 : "0 4px",
          }}
        >
          {!sidebarCollapsed && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0, flex: 1 }}>
              {profile?.logoUrl ? (
                <img
                  src={profile.logoUrl}
                  alt=""
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 8,
                    objectFit: "contain",
                    background: C.bgCardHover,
                    flexShrink: 0,
                  }}
                />
              ) : (
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 8,
                    background: `linear-gradient(135deg, ${C.accent}, ${C.accentLight})`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 20,
                    flexShrink: 0,
                  }}
                >
                  ✈️
                </div>
              )}
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: C.text,
                    lineHeight: 1.1,
                    textTransform: "uppercase",
                    letterSpacing: 0.3,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {empresaLabel}
                </div>
                {mostraNome && (
                  <div
                    style={{
                      fontSize: 10,
                      color: C.textMuted,
                      marginTop: 2,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {profile.nome}
                  </div>
                )}
              </div>
            </div>
          )}
          <button
            onClick={() => isMobile ? setMobileOpen(false) : setCollapsed(!collapsed)}
            style={{
              background: "none",
              border: "none",
              color: C.textMuted,
              cursor: "pointer",
              fontSize: 14,
              padding: 4,
            }}
            aria-label={isMobile ? "Fechar menu" : sidebarCollapsed ? "Expandir menu" : "Recolher menu"}
          >
            {isMobile ? "✕" : sidebarCollapsed ? "▶" : "◀"}
          </button>
        </div>

        {/* Zona do meio: lista de itens rola internamente quando não cabe
            (flex:1 + minHeight:0 + overflow-y:auto), sem empurrar o rodapé.
            minHeight:0 é o que destrava o scroll de um flex child. */}
        <div
          className="sidebar-scroll"
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            WebkitOverflowScrolling: "touch",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}
        >
          {visibleMenu.map((m, i) => {
            // Divider antes do primeiro item ownerOnly — separa a cabine admin
            // do menu normal do tenant.
            const showDivider = m.ownerOnly && (i === 0 || !visibleMenu[i - 1].ownerOnly);
            return (
              <Fragment key={m.id}>
                {showDivider && (
                  <div
                    style={{
                      height: 1,
                      background: C.border,
                      margin: sidebarCollapsed ? "8px 4px" : "8px 8px",
                    }}
                  />
                )}
                <SidebarItem
                  icon={m.icon}
                  label={m.label}
                  active={page === m.id}
                  onClick={() => handleNavigate(m.id)}
                  collapsed={sidebarCollapsed}
                  badge={m.id === "solicitacoes" ? novasSolicitacoes : undefined}
                />
              </Fragment>
            );
          })}
        </div>

        {/* Zona de rodapé: fixa embaixo, nunca encolhe (flexShrink:0). Com a
            zona do meio em flex:1, o rodapé fica colado no fundo sem precisar
            de marginTop:auto — e o "Sair" permanece sempre visível. */}
        <div
          style={{
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            gap: 8,
            paddingTop: 16,
            borderTop: `1px solid ${C.border}`,
          }}
        >
          {!sidebarCollapsed && user && (
            <div
              onClick={() => handleNavigate("perfil")}
              title="Editar meu perfil"
              style={{
                padding: "8px 12px",
                fontSize: 11,
                color: C.textMuted,
                lineHeight: 1.3,
                overflow: "hidden",
                textOverflow: "ellipsis",
                cursor: "pointer",
                borderRadius: 8,
                transition: "background 0.15s",
                background: page === "perfil" ? C.bgCardHover : "transparent",
              }}
            >
              <div style={{ fontWeight: 600, color: C.text, fontSize: 12 }}>
                {user.nome}
              </div>
              <div style={{ marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {user.email}
              </div>
            </div>
          )}
          <ThemeToggle collapsed={sidebarCollapsed} />
          <SidebarItem icon="🚪" label="Sair" onClick={logout} collapsed={sidebarCollapsed} />
          {!sidebarCollapsed && (
            <div
              style={{
                textAlign: "center",
                fontSize: 10,
                color: C.textDim,
                marginTop: 10,
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              Powered by Tecmovel
            </div>
          )}
        </div>
      </div>

      {/* Conteúdo */}
      <div
        style={{
          flex: 1,
          overflow: "auto",
          padding: isMobile ? "60px 14px 20px" : 28,
          transition: "background 0.3s ease",
        }}
      >
        {children}
      </div>

      {/* Botão hambúrguer flutuante — só mobile, só quando drawer fechado.
          position:fixed top-left, alto z-index pra ficar sobre o conteúdo. */}
      {isMobile && !mobileOpen && (
        <button
          onClick={() => setMobileOpen(true)}
          aria-label="Abrir menu"
          style={{
            position: "fixed",
            top: 12,
            left: 12,
            zIndex: 100,
            width: 42,
            height: 42,
            borderRadius: 10,
            background: C.bgCard,
            border: `1px solid ${C.border}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            fontSize: 20,
            color: C.text,
            boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
          }}
        >
          ☰
        </button>
      )}
    </div>
  );
}
